import React, { useState, useEffect } from 'react';
import { Home, Star, Droplet } from 'lucide-react';
import { sortBeersByTapOrder } from './utils/filterUtils';
import * as SupabaseService from './supabaseService';
import { LocationMaintenanceModal } from './modals/kegModals';

export const TapListView = ({ locationName, beers, account, selectedBrewery, locations, onOpenRatingModal, onBackToDashboard }) => {
  const [columnCount, setColumnCount] = useState(2);
  const [isMobile, setIsMobile] = useState(false);

  // Helper to render image
  const renderImage = (keg, account) => (
    <>
      {keg.currentBeer.imageUrl ? (
        <img 
          src={keg.currentBeer.imageUrl} 
          alt={keg.currentBeer.name} 
          className="max-w-full max-h-full object-contain rounded shadow" 
        />      ) : selectedBrewery?.logo_url ? (
        <img 
          src={selectedBrewery.logo_url} 
          alt={selectedBrewery.name} 
          className="max-w-full max-h-full object-contain rounded shadow" 
        />      ) : account?.profile?.logoUrl ? (
        <img 
          src={account.profile.logoUrl} 
          alt={account.profile.name} 
          className="max-w-full max-h-full object-contain rounded shadow" 
        />
      ) : (
        <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center shadow">
          <span className="text-3xl md:text-4xl">🍺</span>
        </div>
      )}
    </>
  );

  // Helper to render beer details (includes Days on Tap)
  const getDaysOnTapLabel = (keg) => {
    const started = keg.current_beer_started_at ? new Date(keg.current_beer_started_at) : null;
    if (!started) return null;
    const now = new Date();
    const diffMs = now - started;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    if (hours >= 1) return `${hours}h`;
    const minutes = Math.floor(diffMs / (1000 * 60));
    return `${minutes}m`;
  };

  const renderBeerDetails = (keg, account) => (
    <>
      <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-1">{keg.currentBeer.name}</h2>
      <p className="text-amber-600 dark:text-amber-400 mb-1 text-xs md:text-sm">{keg.currentBeer.style}</p>
      <p className="text-gray-700 dark:text-gray-300 text-xs md:text-sm">ABV: {keg.currentBeer.abv}% {keg.currentBeer.ibu ? `• IBU: ${keg.currentBeer.ibu}` : ''}</p>
      <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">{keg.currentBeer.brewery || selectedBrewery?.name || account?.profile?.name}</p>
      {keg.currentBeer.description && (
        <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-3">{keg.currentBeer.description}</p>
      )}

      {/* Days on Tap */}
      {getDaysOnTapLabel(keg) && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">On tap: {getDaysOnTapLabel(keg)}</p>
      )}
    </>
  );

  // Helper to get tap display name from location tapNames
  const getTapDisplayName = (keg) => {
    if (!keg.tapNumber || !locations) return keg.tapNumber || '-';
    
    // Parse location and sublocation from locationName
    const [displayLocation, displaySublocation] = locationName.includes(' - ') 
      ? locationName.split(' - ') 
      : [locationName, null];
    
    // Find the location object
    const location = locations.find(l => l.name === displayLocation);
    if (!location) return keg.tapNumber;
    
    // Get tapNames from sublocation or location
    let tapNames = [];
    if (displaySublocation && location.sublocations) {
      const sublocation = location.sublocations.find(s => s.name === displaySublocation);
      tapNames = sublocation?.tapNames || [];
    } else {
      tapNames = location.tapNames || [];
    }
    
    // Look up tap name by index (tapNumber is 1-based, array is 0-based)
    if (tapNames.length > 0) {
      const tapIndex = parseInt(keg.tapNumber) - 1;
      return tapNames[tapIndex] || keg.tapNumber;
    }
    
    return keg.tapNumber;
  };

  // Helper to render tap control
  const renderTapControl = (keg, onOpenRatingModal) => (
    <>
      {/* Tap Number */}
      <div className="flex-1 w-full max-w-[160px] sm:max-w-[200px] flex flex-col items-center justify-center bg-gray-200 dark:bg-gray-700 rounded-lg p-2 gap-1">
        <div className="text-xs md:text-sm text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wide break-words text-center">
          Tap
        </div>
        <div className="text-3xl md:text-4xl font-black text-amber-600 dark:text-amber-500 break-words text-center">
          {getTapDisplayName(keg)}
        </div>
      </div>

      {/* Rate Button */}
      <button 
        onClick={() => onOpenRatingModal(keg)} 
        className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded font-semibold flex items-center justify-center gap-1 transition text-xs flex-shrink-0 w-full max-w-[160px] sm:max-w-[200px]"
      >
        <Star className="w-4 h-4" />
        Rate
      </button>
    </>
  );

  // Load saved column count for this location on mount and when location changes
  useEffect(() => {
    const storageKey = `taplist-columns-${locationName}`;
    const savedColumnCount = localStorage.getItem(storageKey);
    if (savedColumnCount) {
      setColumnCount(parseInt(savedColumnCount));
    }
  }, [locationName]);

  // Track mobile viewport to force single column on small screens
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Location maintenance modal state
  const [showLocationMaintenanceModal, setShowLocationMaintenanceModal] = useState(false);
  const [selectedLocationTaps, setSelectedLocationTaps] = useState([]);
  const [locationMaintenanceForm, setLocationMaintenanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    items: ['Line Cleaning'],
    notes: '',
    taps: [],
    tapsAll: false
  });

  const handleSaveLocationMaintenance = async () => {
    if (!selectedBrewery?.id) {
      alert('No brewery selected');
      return;
    }

    const [loc, sub] = locationName.includes(' - ') ? locationName.split(' - ') : [locationName, null];
    const tapNamesParam = locationMaintenanceForm.tapsAll ? null : (locationMaintenanceForm.taps && locationMaintenanceForm.taps.length > 0 ? locationMaintenanceForm.taps : null);
    const result = await SupabaseService.recordLocationMaintenance(selectedBrewery.id, loc, sub || null, locationMaintenanceForm.items, locationMaintenanceForm.notes, tapNamesParam);

    if (result.success) {
      alert('Location maintenance recorded and batch counters reset.');
      setShowLocationMaintenanceModal(false);
    } else {
      alert('Error recording maintenance: ' + result.error);
    }
  };

  // Save column count when it changes
  const handleColumnChange = (cols) => {
    setColumnCount(cols);
    const storageKey = `taplist-columns-${locationName}`;
    localStorage.setItem(storageKey, cols.toString());
  };
  if (!beers || beers.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-24">
          <div className="px-8 flex items-center justify-between h-full">
            <div className="flex items-center gap-3 h-full">
              <img src="/kegops-logo.png" alt="KegOps" className="h-full object-contain" />
            </div>
            <div className="flex items-center gap-4">
              <span className="text-lg text-gray-700 dark:text-gray-300">{locationName}</span>
              <button 
                onClick={onBackToDashboard}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition"
              >
                <Home className="w-5 h-5" />
                Dashboard
              </button>
            </div>
          </div>
        </div>
        
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">No beers on tap</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">{locationName}</p>
          </div>
        </div>
      </div>
    );
  }

  // Sort by tap order defined in location/sublocation settings
  // Extracts location and sublocation from the locationName display
  const [displayLocation, displaySublocation] = locationName.includes(' - ') 
    ? locationName.split(' - ') 
    : [locationName, null];
  
  const sorted = sortBeersByTapOrder(beers, locations || [], displayLocation, displaySublocation);

  const effectiveColumns = isMobile ? 1 : columnCount;

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-16 sm:h-24 flex-shrink-0">
        <div className="px-4 sm:px-8 flex items-center justify-between h-full">
          <div className="flex items-center gap-3 h-full">
            <img src="/kegops-logo.png" alt="KegOps" className="h-full object-contain" />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Columns:</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4].map(cols => (
                  <button
                    key={cols}
                    onClick={() => handleColumnChange(cols)}
                    className={`px-3 py-1 rounded font-semibold transition text-sm ${
                      columnCount === cols 
                        ? 'bg-amber-600 text-white' 
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {cols}
                  </button>
                ))}
              </div>
            </div>
            <span className="text-lg text-gray-700 dark:text-gray-300">{locationName}</span>

            <button
              onClick={() => {
                const [loc, sub] = locationName.includes(' - ') ? locationName.split(' - ') : [locationName, null];
                // Determine taps for this (sub)location
                const locationObj = (locations || []).find(l => l.name === loc);
                let taps = [];
                if (locationObj) {
                  if (sub && locationObj.sublocations) {
                    const sl = locationObj.sublocations.find(x => x.name === sub);
                    taps = sl?.tapNames || [];
                  } else {
                    taps = locationObj.tapNames || [];
                  }
                }
                setSelectedLocationTaps(taps);
                setLocationMaintenanceForm({ ...locationMaintenanceForm, taps: [], tapsAll: false });
                setShowLocationMaintenanceModal(true);
              }}
              disabled={!selectedBrewery}
              className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
            >
              <Droplet className="w-4 h-4" />
              Line Cleaning
            </button>

            <button 
              onClick={onBackToDashboard}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition"
            >
              <Home className="w-5 h-5" />
              Dashboard
            </button>
          </div>
        </div>
      </div>

      <LocationMaintenanceModal
        show={showLocationMaintenanceModal}
        onClose={() => setShowLocationMaintenanceModal(false)}
        breweryId={selectedBrewery?.id}
        locationName={locationName}
        maintenanceForm={locationMaintenanceForm}
        setMaintenanceForm={setLocationMaintenanceForm}
        onSave={handleSaveLocationMaintenance}
        selectedLocationTaps={selectedLocationTaps}
      />

      {/* Beer List */}
      <div className="flex-1 w-full px-3 sm:px-4 py-3 sm:py-6 overflow-y-auto">
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: `repeat(${effectiveColumns}, minmax(0, 1fr))`,
          gap: '1.5rem',
          height: '100%',
          // Stretch rows to fill space on multi-column layouts; keep single column bounded
          gridAutoRows: effectiveColumns === 1 ? 'clamp(150px, 35vh, 280px)' : 'minmax(150px, 1fr)'
        }}>
          {sorted.map((keg, index) => {
            // Determine layout based on column count and position
            let cardLayout = 'default'; // default: image | info | tap
            if (effectiveColumns === 2) {
              cardLayout = index % 2 === 0 ? 'default' : 'reversed'; // left: default, right: reversed
            } else if (effectiveColumns === 3) {
              if (index % 3 === 0) {
                cardLayout = 'default'; // Left: image | description | tap
              } else if (index % 3 === 1) {
                cardLayout = 'middle'; // Middle: image | tap | description
              } else {
                cardLayout = 'reversed'; // Right: tap | description | image
              }
            } else if (effectiveColumns === 4) {
              const pos = index % 4;
              if (pos === 0) {
                cardLayout = 'default'; // Left: image | description | tap
              } else if (pos === 1) {
                cardLayout = 'reversed'; // Left Middle: tap | description | image
              } else if (pos === 2) {
                cardLayout = 'default'; // Right Middle: image | description | tap
              } else {
                cardLayout = 'reversed'; // Right: tap | description | image
              }
            }

            return (
              <div
                key={keg.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-lg flex flex-col md:flex-row overflow-hidden gap-3 min-h-[150px] h-full max-h-[calc(100vh-12rem)]"
              >
                {/* Render based on layout type */}
                {cardLayout === 'default' && (
                  <>
                    {/* Left: Image */}
                    <div className="flex items-center justify-center flex-1 basis-1/3 min-h-0 max-h-32 sm:max-h-64 md:max-h-96">
                      {renderImage(keg, account)}
                    </div>

                    {/* Center: Beer Details */}
                    <div className="flex-1 basis-1/3 flex flex-col justify-center min-h-0 overflow-hidden">
                      {renderBeerDetails(keg, account)}
                    </div>

                    {/* Right: Tap */}
                    <div className="flex-1 basis-1/3 flex flex-col gap-2 min-h-0 items-end">
                      {renderTapControl(keg, onOpenRatingModal)}
                    </div>
                  </>
                )}

                {cardLayout === 'middle' && (
                  <>
                    {/* Left: Image */}
                    <div className="flex items-center justify-center flex-1 basis-1/3 min-h-0 max-h-32 sm:max-h-64 md:max-h-96">
                      {renderImage(keg, account)}
                    </div>

                    {/* Center: Tap */}
                    <div className="flex-1 basis-1/3 flex flex-col gap-2 min-h-0 items-center justify-center">
                      {renderTapControl(keg, onOpenRatingModal)}
                    </div>

                    {/* Right: Beer Details */}
                    <div className="flex-1 basis-1/3 flex flex-col justify-center min-h-0 overflow-hidden">
                      {renderBeerDetails(keg, account)}
                    </div>
                  </>
                )}

                {cardLayout === 'reversed' && (
                  <>
                    {/* Left: Tap */}
                    <div className="flex-1 basis-1/3 flex flex-col gap-2 min-h-0 items-start">
                      {renderTapControl(keg, onOpenRatingModal)}
                    </div>

                    {/* Center: Beer Details */}
                    <div className="flex-1 basis-1/3 flex flex-col justify-center min-h-0 overflow-hidden">
                      {renderBeerDetails(keg, account)}
                    </div>

                    {/* Right: Image */}
                    <div className="flex items-center justify-center flex-1 basis-1/3 min-h-0 max-h-32 sm:max-h-64 md:max-h-96">
                      {renderImage(keg, account)}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
